/**
 * SimulazioneModal — "come finirebbe se finisse adesso".
 *
 * Andrea 2026-09-13: dalla pagina della partita in corso si calcola
 * l'esito con i voti di questo momento. Il conto lo fa il backend con lo
 * stesso motore del calcolo vero (sostituzioni, fattore capitano,
 * D-Factor, soglie gol); chi deve ancora giocare vale 6 d'ufficio. Non
 * viene salvato niente.
 *
 * Presentazione a LISTA: per ogni squadra gli 11 che contano coi voti,
 * sotto i fattori e il totale, in fondo il risultato finale.
 */

import { useSimulazionePartita } from "../../hooks/useFase5";
import { BloccoSquadra, RisultatoFinale } from "./BloccoSquadra";
import { localeCorrente } from "../../i18n";
import { tr } from "../../i18n";

interface Props {
  partitaId: string;
  nomeCasa: string;
  nomeTrasferta: string;
  onClose: () => void;
}

export function SimulazioneModal({ partitaId, nomeCasa, nomeTrasferta, onClose }: Props) {
  const { data, isLoading, isFetching, error, refetch } = useSimulazionePartita(partitaId, true);
  const errore = error as { response?: { data?: { detail?: string } }; message?: string } | null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto p-2 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-bg-card border border-accent/40 rounded-card w-full max-w-4xl my-2 sm:my-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-primary-soft flex items-start justify-between gap-3">
          <div>
            <div className="font-display text-lg text-accent">{tr("Esito provvisorio")}</div>
            <div className="text-[11px] text-fg-muted">
              {tr("Con i voti di adesso: chi deve ancora giocare vale 6 d'ufficio.")}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-fg-muted hover:text-fg text-2xl leading-none"
            aria-label={tr("Chiudi")}
          >
            ×
          </button>
        </div>

        <div className="p-3 space-y-3">
          {isLoading && (
            <div className="py-10 text-center text-sm text-fg-muted">{tr("Calcolo in corso…")}</div>
          )}
          {errore && !isLoading && (
            <div className="py-6 text-center text-sm text-danger">
              {errore.response?.data?.detail ?? errore.message ?? tr("Calcolo non riuscito")}
            </div>
          )}

          {data && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <BloccoSquadra titolo="🏠 Casa" nome={nomeCasa} s={data.casa} />
                <BloccoSquadra titolo="✈️ Trasferta" nome={nomeTrasferta} s={data.trasferta} />
              </div>

              {/* Risultato finale in fondo */}
              <RisultatoFinale
                etichetta="Se finisse adesso"
                nomeCasa={nomeCasa}
                nomeTrasferta={nomeTrasferta}
                golCasa={data.casa.gol}
                golTrasferta={data.trasferta.gol}
                fpCasa={data.casa.punteggio_totale}
                fpTrasferta={data.trasferta.punteggio_totale}
                squadraCasaId={data.casa.squadra_id}
                squadraTrasfertaId={data.trasferta.squadra_id}
              />

              <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-fg-muted">
                <span>
                  {data.club_da_giocare.length > 0
                    ? tr("6 d'ufficio per chi gioca ancora: {{v1}}.", {
                        v1: data.club_da_giocare.join(", "),
                      })
                    : tr("Tutte le partite dei giocatori schierati hanno i voti.")}{" "}
                  Non viene salvato niente: il risultato vero arriva col calcolo della giornata.
                </span>
                <span className="flex items-center gap-2">
                  <span>
                    alle{" "}
                    {new Date(data.calcolata_at).toLocaleTimeString(localeCorrente(), {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                  <button
                    type="button"
                    onClick={() => refetch()}
                    disabled={isFetching}
                    className="btn-ghost text-xs border border-accent/40 text-accent hover:bg-accent/10 disabled:opacity-50"
                  >
                    {isFetching ? tr("Ricalcolo…") : tr("↻ Ricalcola")}
                  </button>
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
